import { useEffect, useState, memo, type ReactNode } from "react";
import { useRouter } from "next/router";

import { logger } from "@fwa/src/services/logger";
import { useTracking } from "@fwa/src/hooks/useTracking";
import { useFundraiserContext } from "@fwa/src/contexts/FundraiserContext";
import { refreshToken, getToken, shouldRefresh } from "@fwa/src/services/auth";
import { getLoggedInDataState } from "@fwa/src/services/fundraiserService";
import { redirectStateFromUrl } from "@fwa/src/utils/urlUtils";
import { usePrevious } from "@fwa/src/hooks/usePrevious";
import { isBrowser } from "@fwa/src/utils/browserUtils";

// Login refresh

// 1) attempts to periodically (60s) refresh access token used for authenticated requests
// 2) after refresh attempts to update fundraiser state with {oauth}/me for current oauth user and {fws}/me for current fundraiser

export const LoginWrapper = ({ children }: { children: ReactNode }) => {
  const { trackError } = useTracking();
  const [fundraiserState, setFundraiserState] = useFundraiserContext();
  const [refreshCheckCount, setRefreshCheckCount] = useState(0);
  const router = useRouter();

  const prevLoggedInStatus = usePrevious(fundraiserState.loggedInStatus);

  const setLoggedOutState = () => {
    setFundraiserState({
      ...fundraiserState,
      fundraiser: null,
      loggedInStatus: "loggedOut",
    });
  };

  const refresh = (): Promise<void | undefined> =>
    refreshToken()
      .then(() => {
        logger.info("token refreshed");
        return getLoggedInDataState("LoginWrapper");
      })
      .then((postRefreshState) => {
        setFundraiserState({
          ...fundraiserState,
          ...postRefreshState,
        });
        return;
      })
      .catch((err) => {
        logger.error("error refreshing token", err);
        // if we get an error refreshing the token we should log out
        setLoggedOutState();
      });

  // If logging out on fundraiser/** redirect to home page.
  // This should happen when the user clicks on log out but this is just incase refresh fails
  useEffect(() => {
    if (
      prevLoggedInStatus === "loggedIn" &&
      fundraiserState.loggedInStatus === "loggedOut" &&
      (router.pathname.startsWith("/fundraiser/") ||
        router.pathname.startsWith("/signup/") ||
        router.pathname.startsWith("/createpage/"))
    ) {
      router.push({ pathname: "/" }).catch((err) => {
        trackError(err as Error, { component: "LoginWrapper" });
      });
    }
  }, [fundraiserState.loggedInStatus]);

  // /////////////////////////////
  // ////// TOKEN REFRESH  ///////
  // /////////////////////////////
  useEffect(() => {
    // Don't need to poll for token refresh if we are not logged in
    if (!getToken()) return;

    const timer = setTimeout(() => {
      if (shouldRefresh()) {
        refresh().catch((err) => {
          trackError(err as Error, { component: "LoginWrapper" });
        });
      }
      setRefreshCheckCount(refreshCheckCount + 1);
    }, 60_000);
    return () => clearTimeout(timer);
    // we need fundraiser state as a dep so we can run again after log in
  }, [refreshCheckCount, setRefreshCheckCount, fundraiserState]);

  // /////////////////////////////////////////////////////
  // ////// CHECK IF ALREADY LOGGED IN WHEN APP OPENS ////
  // ////////////////////////////////////////////////////

  useEffect(() => {
    if (!router.isReady) return;
    if (!isBrowser) return;
    const { code } = router.query;
    const { isAmJourney, isEmJourney } = redirectStateFromUrl(
      window.location.href,
    );
    const isOnAuthPath = router.pathname === "/auth";

    // check if we are in EM or AM journey

    if (getToken()) {
      // if we have a token check if we are logged in
      logger.log("already have authenticated token");
      getLoggedInDataState("LoginWrapper")
        .then((currentLoggedInDataState) => {
          const { oauthUser, fundraiser } = currentLoggedInDataState;
          // set state with current logged in data
          setFundraiserState({
            ...fundraiserState,
            ...currentLoggedInDataState,
          });
          // If we are not in an AM or EM journey and we have an oauthUser but no fundraiser,
          // assume they are half way though the DIY journey need to sign up and redirect to signup page
          // auth page will handle this if we are on an auth path already
          if (
            !(
              isAmJourney ||
              isEmJourney ||
              router.pathname === "/teamsignup"
            ) &&
            !isOnAuthPath &&
            oauthUser &&
            !fundraiser
          ) {
            router?.push({
              pathname: "/signup",
              query: { ...router.query },
            });
          }
          return;
        })
        .catch((err) => {
          // this should never throw because we return error not throw them but just in case log out
          logger.error("error using existing token", err);
          setLoggedOutState();
        });
    } else {
      // if we have code we might be in the process of logging in so don't log out
      if (code) return;
      // if we don't have a token set state to logged out
      logger.info("no login codes");
      setLoggedOutState();
    }
  }, [router.isReady]);

  return <>{children}</>;
};

export default memo(LoginWrapper);
